"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SelectInput = exports.SelectInputElement = void 0;
// SelectInput.ts
const web_components_1 = require("../web-components");
const SelectInputStyles_1 = require("./SelectInputStyles");
class SelectInputElement extends HTMLElement {
    triggerEl;
    valueEl;
    hiddenInputEl;
    menuEl;
    searchEl;
    listEl;
    internalId = `select-input-${Math.random().toString(16).slice(2)}`;
    isOpen = false;
    query = "";
    activeValue = null;
    static get observedAttributes() {
        return ["label", "value", "name", "disabled", "placeholder", "options"];
    }
    constructor() {
        super();
    }
    connectedCallback() {
        // Keep a stable id across renders (don’t use Date.now() each render)
        const idAttr = this.getAttribute("id");
        if (idAttr)
            this.internalId = idAttr;
        // close on outside click
        document.addEventListener("mousedown", this.onDocMouseDown, true);
        this.render();
    }
    disconnectedCallback() {
        document.removeEventListener("mousedown", this.onDocMouseDown, true);
    }
    attributeChangedCallback() {
        this.render();
    }
    // public API
    get value() {
        return this.getAttribute("value") ?? "";
    }
    set value(v) {
        if (v === null || v === undefined || v === "")
            this.removeAttribute("value");
        else
            this.setAttribute("value", v);
    }
    setOptions(options) {
        this.setAttribute("options", JSON.stringify(options));
    }
    parseOptionsAttribute() {
        const rawOptions = this.getAttribute("options") || "[]";
        try {
            const optionsArray = JSON.parse(rawOptions);
            if (!Array.isArray(optionsArray))
                return [];
            return optionsArray;
        }
        catch {
            console.warn("[SelectInput] Invalid JSON in `options` attribute");
            return [];
        }
    }
    normalizeOptions(options) {
        // Supports:
        // 1) SelectOption[]
        // 2) [{ heading, options:[...] }, ...]
        // 3) Flat w/ group: [{ value,label,group:"Private..." }, ...]
        const groups = [];
        const byHeading = new Map();
        const pushUngrouped = (opt) => {
            if (!groups[0] || groups[0].heading)
                groups.unshift({ options: [] });
            groups[0].options.push(opt);
        };
        for (const item of options) {
            const isGroup = item && // it is not null/undefined
                typeof item === "object" && // it is an object
                "options" in item && // it has options key
                Array.isArray(item.options); // options is an array
            if (isGroup) {
                const group = item;
                const heading = String(group.heading ?? "").trim();
                const opts = (group.options ?? [])
                    .map((option) => ({
                    value: String(option?.value ?? ""),
                    label: String(option?.label ?? ""),
                    disabled: !!option?.disabled,
                }))
                    .filter((option) => option.value !== "" && option.label !== "");
                groups.push({ heading, options: opts });
                continue;
            }
            const optRaw = item;
            const opt = {
                value: String(optRaw?.value ?? ""),
                label: String(optRaw?.label ?? ""),
                disabled: !!optRaw?.disabled,
                group: optRaw?.group ? String(optRaw.group) : undefined,
            };
            if (!opt.value || !opt.label)
                continue;
            if (!opt.group) {
                pushUngrouped(opt);
                continue;
            }
            const heading = opt.group;
            let g = byHeading.get(heading);
            if (!g) {
                g = { heading, options: [] };
                byHeading.set(heading, g);
                groups.push(g);
            }
            g.options.push(opt);
        }
        // Ensure at least one list exists
        if (groups.length === 0)
            groups.push({ options: [] });
        return groups;
    }
    getSelectedLabel(groups, value) {
        for (const group of groups) {
            const found = group.options.find((option) => option.value === value);
            if (found)
                return found.label;
        }
        return "";
    }
    getVisibleFlatOptions(groups, q) {
        const query = q.trim().toLowerCase();
        const visible = [];
        for (const group of groups) {
            for (const option of group.options) {
                if (!query || option.label.toLowerCase().includes(query))
                    visible.push(option);
            }
        }
        return visible;
    }
    setValueAndEmit(nextValue) {
        this.setAttribute("value", nextValue);
        if (this.hiddenInputEl)
            this.hiddenInputEl.value = nextValue; // keep hidden input in sync for forms
        this.dispatchEvent(new Event("change", { bubbles: true }));
    }
    openMenu() {
        if (this.hasAttribute("disabled"))
            return;
        this.isOpen = true;
        // default active to current value, else first visible
        const groups = this.normalizeOptions(this.parseOptionsAttribute());
        const visible = this.getVisibleFlatOptions(groups, this.query);
        this.activeValue =
            this.value ||
                visible.find((option) => !option.disabled)?.value ||
                null;
        this.render();
        requestAnimationFrame(() => this.searchEl?.focus());
    }
    closeMenu() {
        this.isOpen = false;
        this.query = "";
        this.activeValue = null;
        this.render();
        this.triggerEl?.focus();
    }
    toggleMenu() {
        if (this.isOpen)
            this.closeMenu();
        else
            this.openMenu();
    }
    onDocMouseDown = (e) => {
        if (!this.isOpen)
            return;
        const path = e.composedPath?.() ?? [];
        const clickedInside = path.includes(this);
        if (!clickedInside)
            this.closeMenu();
    };
    onTriggerClick = (e) => {
        e.preventDefault();
        if (this.hasAttribute("disabled"))
            return;
        this.toggleMenu();
    };
    onSearchInput = (e) => {
        // Prevent native input event from bubbling out of the web component
        e.stopPropagation();
        const target = e.target;
        this.query = target.value ?? "";
        // reset active to first visible match
        const groups = this.normalizeOptions(this.parseOptionsAttribute());
        const visible = this.getVisibleFlatOptions(groups, this.query).filter((option) => !option.disabled);
        this.activeValue = visible[0]?.value ?? null;
        // update options list without re-rendering the whole component
        this.updateListOnly();
    };
    moveActive(delta) {
        // Delta is the number of steps to move the active selection.
        // A positive delta moves the selection down,
        // while a negative delta moves it up.
        // Maybe call it moveSelectionBy or moveActiveIndexBy?
        const groups = this.normalizeOptions(this.parseOptionsAttribute());
        const visible = this.getVisibleFlatOptions(groups, this.query).filter((option) => !option.disabled);
        if (visible.length === 0)
            return;
        const index = Math.max(0, visible.findIndex((option) => option.value === this.activeValue));
        let next = index + delta;
        if (next < 0)
            next = visible.length - 1;
        if (next >= visible.length)
            next = 0;
        this.activeValue = visible[next].value;
        // update only the list so the search input keeps its caret/focus
        this.updateListOnly();
        // scroll into view
        requestAnimationFrame(() => {
            const el = this.querySelector(`[data-option-value="${CSS.escape(this.activeValue ?? "")}"]`);
            el?.scrollIntoView({ block: "nearest" });
            this.searchEl?.focus();
        });
    }
    onKeyDown = (e) => {
        if (this.hasAttribute("disabled"))
            return;
        if (!this.isOpen) {
            if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                this.openMenu();
            }
            return;
        }
        if (e.key === "Escape") {
            e.preventDefault();
            this.closeMenu();
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            this.moveActive(1);
            return;
        }
        if (e.key === "ArrowUp") {
            e.preventDefault();
            this.moveActive(-1);
            return;
        }
        if (e.key === "Enter") {
            e.preventDefault();
            if (!this.activeValue)
                return;
            this.setValueAndEmit(this.activeValue);
            this.closeMenu();
            return;
        }
    };
    onOptionClick = (e) => {
        const target = e.target;
        const optionEl = target.closest("[data-option-value]");
        if (!optionEl)
            return;
        const value = optionEl.getAttribute("data-option-value") ?? "";
        const disabled = optionEl.getAttribute("data-disabled") === "true";
        if (!value || disabled)
            return;
        this.setValueAndEmit(value);
        this.closeMenu();
    };
    buildListMarkup(groups, queryRaw) {
        const query = queryRaw.trim().toLowerCase();
        const groupMarkup = groups
            .map((g) => {
            const visible = g.options.filter((o) => !query ? true : o.label.toLowerCase().includes(query));
            if (visible.length === 0)
                return "";
            const heading = g.heading?.trim();
            const headingMarkup = heading
                ? `<div class="SelectInput__groupHeading" role="presentation">${(0, web_components_1.escapeHTMLChars)(heading)}</div>`
                : "";
            const optionsMarkup = visible
                .map((option) => {
                const isSelected = this.value === option.value;
                const isActive = this.activeValue === option.value;
                const classes = [
                    "SelectInput__option",
                    isSelected ? "is-selected" : "",
                    isActive ? "is-active" : "",
                    option.disabled ? "is-disabled" : "",
                ]
                    .filter(Boolean)
                    .join(" ");
                return `
                            <div
                            class="${classes}"
                            role="option"
                            aria-selected="${isSelected ? "true" : "false"}"
                            data-option-value="${(0, web_components_1.escapeHTMLChars)(option.value)}"
                            data-disabled="${option.disabled ? "true" : "false"}"
                            title="${(0, web_components_1.escapeHTMLChars)(option.label)}"
                            >
                                ${(0, web_components_1.escapeHTMLChars)(option.label)}
                            </div>
                        `;
            })
                .join("");
            return `${headingMarkup}${optionsMarkup}`;
        })
            .join("");
        const hasAnyResults = this.getVisibleFlatOptions(groups, queryRaw).length > 0;
        return {
            markup: hasAnyResults
                ? groupMarkup
                : `<div class="SelectInput__empty" role="presentation">No results</div>`,
            hasAnyResults,
        };
    }
    updateListOnly() {
        if (!this.listEl)
            return;
        const groups = this.normalizeOptions(this.parseOptionsAttribute());
        const { markup } = this.buildListMarkup(groups, this.query);
        // update only the list; keep the input element intact (no caret weirdness)
        this.listEl.innerHTML = markup;
    }
    render() {
        // Log component render
        console.log("render(): [SelectInput] Rendering component");
        const label = this.getAttribute("label") ?? "Label";
        const name = this.getAttribute("name") ?? "";
        const disabled = this.hasAttribute("disabled");
        const placeholder = this.getAttribute("placeholder") ?? "None selected";
        const parsed = this.parseOptionsAttribute();
        const groups = this.normalizeOptions(parsed);
        const selectedLabel = this.getSelectedLabel(groups, this.value);
        const displayText = selectedLabel || placeholder;
        const isPlaceholder = !selectedLabel;
        const query = this.query.trim().toLowerCase();
        const groupMarkup = groups
            .map((g) => {
            const visible = g.options.filter((o) => !query ? true : o.label.toLowerCase().includes(query));
            if (visible.length === 0)
                return "";
            const heading = g.heading?.trim();
            const headingMarkup = heading
                ? `<div class="SelectInput__groupHeading" role="presentation">${(0, web_components_1.escapeHTMLChars)(heading)}</div>`
                : "";
            const optionsMarkup = visible
                .map((option) => {
                const isSelected = this.value === option.value;
                const isActive = this.activeValue === option.value;
                const classes = [
                    "SelectInput__option",
                    isSelected ? "is-selected" : "",
                    isActive ? "is-active" : "",
                    option.disabled ? "is-disabled" : "",
                ]
                    .filter(Boolean)
                    .join(" ");
                return `
                            <div
                                class="${classes}"
                                role="option"
                                aria-selected="${isSelected ? "true" : "false"}"
                                data-option-value="${(0, web_components_1.escapeHTMLChars)(option.value)}"
                                data-disabled="${option.disabled ? "true" : "false"}"
                                title="${(0, web_components_1.escapeHTMLChars)(option.label)}"
                            >
                                ${(0, web_components_1.escapeHTMLChars)(option.label)}
                            </div>
                        `;
            })
                .join("");
            return `${headingMarkup}${optionsMarkup}`;
        })
            .join("");
        const hasAnyResults = this.getVisibleFlatOptions(groups, this.query).length > 0;
        this.innerHTML = `
            <style>
                /* scoped to the element */
                select-input { ${SelectInputStyles_1.styles.host} }

                select-input .SelectInputWrap { ${SelectInputStyles_1.styles.wrap} }

                select-input .SelectInput { ${SelectInputStyles_1.styles.selectInput} }
                select-input .SelectInput:focus-visible { ${SelectInputStyles_1.styles.focusVisible} }
                select-input .SelectInput.is-disabled { ${SelectInputStyles_1.styles.disabledTrigger} }

                select-input .SelectInput__texts { ${SelectInputStyles_1.styles.texts} }
                select-input .SelectInput__label { ${SelectInputStyles_1.styles.label} }
                select-input .SelectInput__value { ${SelectInputStyles_1.styles.value} }
                select-input .SelectInput__placeholder { ${SelectInputStyles_1.styles.placeholder} }
                select-input .SelectInput__icons { ${SelectInputStyles_1.styles.icons} }

                select-input .SelectInput__menu { ${SelectInputStyles_1.styles.menu} }
                select-input .SelectInput__menu[hidden] { display: none; }

                select-input .SelectInput__searchRow { ${SelectInputStyles_1.styles.searchRow} }
                select-input .SelectInput__search { ${SelectInputStyles_1.styles.search} }
                select-input .SelectInput__search:focus { ${SelectInputStyles_1.styles.searchFocus} }

                select-input .SelectInput__list { ${SelectInputStyles_1.styles.list} }

                select-input .SelectInput__groupHeading { ${SelectInputStyles_1.styles.groupHeading} }

                select-input .SelectInput__option { ${SelectInputStyles_1.styles.option} }
                select-input .SelectInput__option.is-selected { ${SelectInputStyles_1.styles.optionSelected} }
                select-input .SelectInput__option.is-active:not(.is-selected) { ${SelectInputStyles_1.styles.optionActive} }
                select-input .SelectInput__option.is-disabled { ${SelectInputStyles_1.styles.optionDisabled} }

                select-input .SelectInput__empty { ${SelectInputStyles_1.styles.empty} }
            </style>

            <div class="SelectInputWrap" data-select-input-id="${(0, web_components_1.escapeHTMLChars)(this.internalId)}">
                <div class="SelectInput ${disabled ? "is-disabled" : ""}"
                    role="combobox"
                    aria-expanded="${this.isOpen ? "true" : "false"}"
                    aria-disabled="${disabled ? "true" : "false"}"
                    aria-haspopup="listbox"
                    tabindex="${disabled ? "-1" : "0"}"
                >
                    <div class="SelectInput__texts">
                        <div class="SelectInput__label" title="${(0, web_components_1.escapeHTMLChars)(label)}">
                            ${(0, web_components_1.escapeHTMLChars)(label)}
                        </div>
                        <span class="SelectInput__value ${isPlaceholder ? "SelectInput__placeholder" : ""}">
                            ${(0, web_components_1.escapeHTMLChars)(displayText)}
                        </span>
                    </div>

                    <span class="SelectInput__icons" aria-hidden="true">
                        <dc-icon family="regular" name="chevron-down" size="md"></dc-icon>
                    </span>
                </div>

                <input type="hidden" class="SelectInput__hidden" ${name ? `name="${(0, web_components_1.escapeHTMLChars)(name)}"` : ""} value="${(0, web_components_1.escapeHTMLChars)(this.value)}" />

                <div class="SelectInput__menu" role="presentation" ${this.isOpen ? "" : "hidden"}>
                    <div class="SelectInput__searchRow">
                        <input
                        class="SelectInput__search"
                        type="text"
                        value="${(0, web_components_1.escapeHTMLChars)(this.query)}"
                        placeholder="Search"
                        ${disabled ? "disabled" : ""}
                        aria-label="Search options"
                        />
                    </div>

                    <div class="SelectInput__list" role="listbox">
                        ${hasAnyResults
            ? groupMarkup
            : `<div class="SelectInput__empty" role="presentation">No results</div>`}
                    </div>
                </div>
            </div>
        `;
        // wire elements
        this.triggerEl = this.querySelector(".SelectInput");
        this.valueEl = this.querySelector(".SelectInput__value");
        this.hiddenInputEl = this.querySelector(".SelectInput__hidden");
        this.menuEl = this.querySelector(".SelectInput__menu");
        this.searchEl = this.querySelector(".SelectInput__search");
        this.listEl = this.querySelector(".SelectInput__list");
        // listeners (rebind each render)
        this.triggerEl.onclick = null;
        this.triggerEl.onkeydown = null;
        this.searchEl.oninput = null;
        this.searchEl.onkeydown = null;
        this.listEl.onclick = null;
        this.triggerEl.addEventListener("click", this.onTriggerClick);
        this.triggerEl.addEventListener("keydown", this.onKeyDown);
        // Stop native change/input from search input from escaping component
        this.searchEl.addEventListener("change", (ev) => ev.stopPropagation());
        this.searchEl.addEventListener("input", this.onSearchInput);
        this.searchEl.addEventListener("keydown", this.onKeyDown);
        this.listEl.addEventListener("click", this.onOptionClick);
    }
}
exports.SelectInputElement = SelectInputElement;
const SelectInput = (props) => {
    const { label, name, value, placeholder, disabled, options } = props.attributes;
    const id = props.attributes.id || `select-button-${Date.now()}`;
    const optionsAttr = options && options.length > 0
        ? `options='${JSON.stringify(options).replace(/'/g, "&apos;")}'`
        : "";
    return `<select-input
        ${(0, web_components_1.toAttribute)("id", id)}
        ${(0, web_components_1.toAttribute)("label", label ?? "Track")}
        ${(0, web_components_1.toAttribute)("name", name)}
        ${(0, web_components_1.toAttribute)("value", value)}
        ${(0, web_components_1.toAttribute)("placeholder", placeholder ?? "None selected")}
         ${optionsAttr}
        ${disabled ? "disabled" : ""}>
    </select-input>`;
};
exports.SelectInput = SelectInput;
(0, web_components_1.registerWebComponent)("select-input", SelectInputElement);
//# sourceMappingURL=SelectInput.js.map